(() => {
  if (window.__listicleBuyboxALoaded) return;
  window.__listicleBuyboxALoaded = true;

  const ROOT_SELECTOR = '[data-listicle-buybox-a]';
  const BOGO_RELOAD_MARKER = '_lba_bogo_reload';

  const moneyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const formatMoney = (cents) => {
    const value = Number(cents || 0) / 100;
    return moneyFormatter.format(value);
  };

  const formatMoneySplit = (cents) => {
    const value = Math.max(0, Math.round(Number(cents || 0)));
    const dollars = Math.floor(value / 100);
    const decimals = String(value % 100).padStart(2, '0');

    return `
      <span class="listicle-buybox-a__price-currency">$</span>
      <span class="listicle-buybox-a__price-whole">${dollars.toLocaleString('en-US')}</span>
      <span class="listicle-buybox-a__price-decimal">.${decimals}</span>
    `;
  };

  const clampInt = (value, min = 1) => {
    const number = Math.floor(Number(value) || 0);
    return Math.max(min, number);
  };

  const parseCodes = (raw) =>
    String(raw || '')
      .split(/[\n,]+/g)
      .map((value) => value.trim())
      .filter(Boolean);

  const normalizeParamString = (rawValue) => {
    if (!rawValue) return '';

    let normalized = String(rawValue).trim();
    if (!normalized) return '';

    if (normalized.includes('://')) {
      try {
        normalized = new URL(normalized).search.replace(/^\?/, '');
      } catch (error) {}
    }

    const questionIndex = normalized.indexOf('?');
    if (questionIndex > -1) normalized = normalized.slice(questionIndex + 1);

    normalized = normalized.replace(/^[?&]+/, '');

    const hashIndex = normalized.indexOf('#');
    if (hashIndex > -1) normalized = normalized.slice(0, hashIndex);

    return normalized.trim();
  };

  const mergeParamStrings = (baseRaw, overrideRaw) => {
    const merged = new URLSearchParams(normalizeParamString(baseRaw));
    const override = new URLSearchParams(normalizeParamString(overrideRaw));

    override.forEach((value, key) => {
      if (!key) return;
      merged.set(key, value);
    });

    return merged.toString();
  };

  const isInternalParam = (key) => key === BOGO_RELOAD_MARKER;

  const postJSON = async (url, body = {}) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      cache: 'no-store',
      body: JSON.stringify(body),
    });

    const contentType = response.headers.get('content-type') || '';
    const data = contentType.includes('application/json')
      ? await response.json()
      : await response.text();

    if (!response.ok) {
      const error = new Error(
        (data && (data.description || data.message)) ||
          (typeof data === 'string' ? data : 'Request failed')
      );
      error.status = response.status;
      error.payload = data;
      throw error;
    }

    return data;
  };

  const getConfiguredBogoParams = (root, quantityData = null) => {
    if (root.dataset.bogoEnabled !== 'true') return '';

    const globalParams = root.dataset.bogoParams || '';
    const quantityParams = quantityData?.bogoParams || '';

    return mergeParamStrings(globalParams, quantityParams);
  };

  const getAllConfiguredBogoKeys = (root) => {
    const keys = new Set();

    const collect = (raw) => {
      const params = new URLSearchParams(normalizeParamString(raw));
      params.forEach((value, key) => {
        if (key && !isInternalParam(key)) keys.add(key);
      });
    };

    collect(root.dataset.bogoParams || '');
    root.querySelectorAll('[data-lba-quantity]').forEach((button) => {
      collect(button.dataset.bogoParams || '');
    });

    return keys;
  };

  const removeInactiveBogoParams = (root, effectiveParams) => {
    const configuredKeys = getAllConfiguredBogoKeys(root);
    if (!configuredKeys.size) return;

    const effective = new URLSearchParams(effectiveParams);
    const url = new URL(window.location.href);
    let changed = false;

    configuredKeys.forEach((key) => {
      if (!effective.has(key) && url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    });

    if (changed) {
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    }
  };

  const publicSearchParams = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete(BOGO_RELOAD_MARKER);
    return params;
  };

  const bogoParamsMatchCurrentUrl = (paramsString) => {
    if (!paramsString) return true;

    const configured = new URLSearchParams(paramsString);
    const current = new URLSearchParams(window.location.search);
    let matches = true;

    configured.forEach((value, key) => {
      if (!key || isInternalParam(key)) return;
      if (current.get(key) !== value) matches = false;
    });

    return matches;
  };

  const restoreScroll = (state) => {
    const x = Number(state?.scrollX);
    const y = Number(state?.scrollY);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;

    const restore = () => window.scrollTo(Math.max(0, x), Math.max(0, y));
    restore();
    requestAnimationFrame(() => {
      restore();
      requestAnimationFrame(restore);
    });
    window.setTimeout(restore, 120);
  };

  const readBogoReloadState = (root) => {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get(BOGO_RELOAD_MARKER);
    if (!raw) return null;

    let state = null;

    try {
      state = JSON.parse(raw);
    } catch (error) {}

    if (!state || state.sectionId !== root.dataset.sectionId) return null;

    url.searchParams.delete(BOGO_RELOAD_MARKER);
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);

    return state;
  };

  const reloadForBogoIfNeeded = (root, state, quantityData = null) => {
    const paramsString = getConfiguredBogoParams(root, quantityData);
    removeInactiveBogoParams(root, paramsString);

    if (!paramsString || bogoParamsMatchCurrentUrl(paramsString)) return false;

    const url = new URL(window.location.href);
    const configured = new URLSearchParams(paramsString);

    getAllConfiguredBogoKeys(root).forEach((key) => {
      url.searchParams.delete(key);
    });

    configured.forEach((value, key) => {
      if (!key || isInternalParam(key)) return;
      url.searchParams.set(key, value);
    });

    url.searchParams.set(
      BOGO_RELOAD_MARKER,
      JSON.stringify({
        sectionId: root.dataset.sectionId || '',
        quantity: state.quantity,
        purchaseType: state.purchaseType,
        sellingPlan: state.sellingPlan || '',
        submit: true,
        scrollX: Math.round(window.scrollX || 0),
        scrollY: Math.round(window.scrollY || 0),
      })
    );

    window.location.replace(url.toString());
    return true;
  };

  const buildDestinationUrl = (root, destination, quantityData = null) => {
    const base = destination === 'cart' ? '/cart' : '/checkout';
    const url = new URL(base, window.location.origin);

    publicSearchParams().forEach((value, key) => {
      if (!key || isInternalParam(key)) return;
      url.searchParams.set(key, value);
    });

    const bogoParams = new URLSearchParams(
      getConfiguredBogoParams(root, quantityData)
    );
    bogoParams.forEach((value, key) => {
      if (!key || isInternalParam(key)) return;
      url.searchParams.set(key, value);
    });

    const discountCodes = Array.from(
      new Set([
        ...parseCodes(root.dataset.discountCodes),
        ...parseCodes(quantityData?.discountCodes || ''),
      ])
    );

    if (discountCodes.length) {
      url.searchParams.set('discount', discountCodes.join(','));
    }

    return url.pathname + url.search + url.hash;
  };

  const initBuybox = (root) => {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const galleryImage = root.querySelector('[data-lba-main-image]');
    const thumbs = Array.from(root.querySelectorAll('[data-lba-thumb]'));
    const prevButton = root.querySelector('[data-lba-gallery-prev]');
    const nextButton = root.querySelector('[data-lba-gallery-next]');

    const quantityOptions = root.querySelector('[data-lba-quantity-options]');
    const quantityButtons = Array.from(root.querySelectorAll('[data-lba-quantity]'));
    const purchaseTypeInputs = Array.from(
      root.querySelectorAll('[data-lba-purchase-type]')
    );

    const subscriptionCard = root.querySelector(
      '.listicle-buybox-a__purchase-card--subscription'
    );
    const oneTimeCard = root.querySelector(
      '.listicle-buybox-a__purchase-card--onetime'
    );

    const mobileQuantity = root.querySelector('[data-lba-mobile-quantity]');
    const mobileTrigger = root.querySelector('[data-lba-mobile-quantity-trigger]');
    const mobileOptions = Array.from(root.querySelectorAll('[data-lba-mobile-option]'));
    const mobileTitle = root.querySelector('[data-lba-mobile-quantity-title]');
    const mobileServings = root.querySelector('[data-lba-mobile-servings]');
    const mobileCompare = root.querySelector('[data-lba-mobile-compare]');
    const mobilePrice = root.querySelector('[data-lba-mobile-price]');
    const mobileBadge = root.querySelector('[data-lba-mobile-badge]');

    const form = root.querySelector('[data-lba-form]');
    const variantInput = root.querySelector('[data-lba-form-variant]');
    const quantityInput = root.querySelector('[data-lba-form-quantity]');
    const sellingPlanInput = root.querySelector('[data-lba-form-selling-plan]');
    const frequencySelect = root.querySelector('[data-lba-frequency]');

    const subscriptionCompare = root.querySelector('[data-lba-sub-compare]');
    const subscriptionPrice = root.querySelector('[data-lba-sub-price]');
    const oneTimePrice = root.querySelector('[data-lba-otp-price]');
    const submitButton = root.querySelector('[data-lba-submit]');
    const deliveryDate = root.querySelector('[data-lba-delivery-date]');
    const statusMessage = root.querySelector('[data-lba-status]');

    const ctaText = root.dataset.ctaText || 'Add to Cart';
    const soldOutText = root.dataset.soldOutText || 'Sold Out';
    const addingText = root.dataset.addingText || 'Adding...';
    const addedText = root.dataset.addedText || 'Added to Cart ✓';
    const bagLabel = root.dataset.bagLabel || 'Bag';
    const servingsPerUnit = clampInt(root.dataset.servingsPerUnit || 30, 1);
    const popularBadge = root.dataset.popularBadge || 'Most Popular';
    const dealBadge = root.dataset.dealBadge || 'Best Deal';

    let activeGalleryIndex = 0;
    let selectedQuantity = clampInt(root.dataset.defaultQuantity || 2, 1);
    selectedQuantity = Math.min(3, selectedQuantity);
    let purchaseType = root.dataset.defaultPurchase || 'subscription';
    let available = root.dataset.available === 'true';
    let busy = false;

    const quantityData = {};

    quantityButtons.forEach((button) => {
      const quantity = Number(button.dataset.quantity);
      const availabilityKey = `available${quantity}`;

      quantityData[quantity] = {
        quantity,
        variantId: Number(button.dataset.variantId || 0),
        cartQuantity: clampInt(button.dataset.cartQuantity || quantity, 1),
        available: quantityOptions?.dataset?.[availabilityKey] !== 'false',
        compare: Number(button.dataset.compare || 0),
        subscriptionOverride: Number(button.dataset.subPrice || 0),
        onetimeOverride: Number(button.dataset.otpPrice || 0),
        defaultPlan: String(button.dataset.defaultPlan || ''),
        discountCodes: String(button.dataset.discountCodes || ''),
        bogoParams: String(button.dataset.bogoParams || ''),
        button,
      };
    });

    if (!quantityData[selectedQuantity]) {
      selectedQuantity = Number(Object.keys(quantityData)[0]) || 1;
    }

    if (!subscriptionCard) purchaseType = 'onetime';

    const getCurrentQuantityData = () =>
      quantityData[selectedQuantity] || quantityData[1] || null;

    const planUnitPrice = (planId = '') => {
      if (!frequencySelect) return 0;

      const option = planId
        ? Array.from(frequencySelect.options).find(
            (item) => item.value === String(planId)
          )
        : frequencySelect.selectedOptions?.[0];

      const price = Number(option?.dataset?.unitPrice || 0);
      return price > 0 ? price : 0;
    };

    const subscriptionPriceFor = (data, useCurrentPlan = false) => {
      if (!data) return 0;
      if (data.subscriptionOverride > 0) return data.subscriptionOverride;

      const unitPrice = useCurrentPlan
        ? planUnitPrice()
        : planUnitPrice(data.defaultPlan) || planUnitPrice();

      return unitPrice > 0 ? unitPrice * data.quantity : 0;
    };

    const oneTimePriceFor = (data) => {
      if (!data) return 0;
      if (data.onetimeOverride > 0) return data.onetimeOverride;
      return data.compare;
    };

    const setAvailability = (isAvailable) => {
      available = !!isAvailable;
      root.dataset.available = available ? 'true' : 'false';
      root.classList.toggle('is-sold-out', !available);

      if (submitButton) {
        submitButton.disabled = !available || busy;
        if (!busy) submitButton.textContent = available ? ctaText : soldOutText;
      }

      if (statusMessage) {
        if (!available) {
          statusMessage.textContent = soldOutText;
        } else if (statusMessage.textContent === soldOutText) {
          statusMessage.textContent = '';
        }
      }
    };

    const setBusy = (isBusy, label = '') => {
      busy = !!isBusy;

      if (!submitButton) return;

      submitButton.classList.toggle('is-loading', busy);
      submitButton.disabled = busy || !available;

      if (label) {
        submitButton.textContent = label;
      } else if (!busy) {
        submitButton.textContent = available ? ctaText : soldOutText;
      }
    };

    const updateDeliveryDate = () => {
      if (!deliveryDate) return;

      const businessDaysTarget = clampInt(root.dataset.deliveryBusinessDays || 4, 1);
      const date = new Date();
      let businessDaysAdded = 0;

      while (businessDaysAdded < businessDaysTarget) {
        date.setDate(date.getDate() + 1);
        const day = date.getDay();
        if (day !== 0 && day !== 6) businessDaysAdded += 1;
      }

      deliveryDate.textContent = date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
      });
    };

    const preloadAdjacentGalleryImage = (index) => {
      if (!thumbs.length) return;
      const nextIndex = (index + 1) % thumbs.length;
      const nextSrc = thumbs[nextIndex]?.dataset?.image;
      if (!nextSrc) return;

      const image = new Image();
      image.decoding = 'async';
      image.src = nextSrc;
    };

    const updateGallery = (index) => {
      if (!thumbs.length || !galleryImage) return;

      if (index < 0) index = thumbs.length - 1;
      if (index >= thumbs.length) index = 0;

      activeGalleryIndex = index;
      const thumb = thumbs[index];

      if (thumb.dataset.image) galleryImage.src = thumb.dataset.image;
      if (thumb.dataset.srcset) galleryImage.srcset = thumb.dataset.srcset;
      if (thumb.dataset.sizes) galleryImage.sizes = thumb.dataset.sizes;
      galleryImage.alt = thumb.dataset.alt || '';

      thumbs.forEach((item, itemIndex) => {
        const isActive = itemIndex === index;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      preloadAdjacentGalleryImage(index);
    };

    const selectPlanForQuantity = () => {
      if (!frequencySelect) return;

      const data = getCurrentQuantityData();
      if (!data) return;

      const options = Array.from(frequencySelect.options);
      if (!options.length) return;

      let targetIndex = data.defaultPlan
        ? options.findIndex((option) => option.value === data.defaultPlan)
        : -1;

      if (targetIndex < 0) {
        targetIndex = Math.min(selectedQuantity - 1, options.length - 1);
      }

      frequencySelect.selectedIndex = Math.max(0, targetIndex);

      if (sellingPlanInput) {
        sellingPlanInput.value = frequencySelect.value;
      }
    };

    const updateQuantityCards = () => {
      quantityButtons.forEach((button) => {
        const quantity = Number(button.dataset.quantity);
        const data = quantityData[quantity];
        if (!data) return;

        const isSelected = quantity === selectedQuantity;
        const cardPrice = button.querySelector('[data-lba-card-price]');
        const cardCompare = button.querySelector('[data-lba-card-compare]');

        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

        if (cardCompare) cardCompare.textContent = formatMoney(data.compare);
        if (cardPrice) {
          cardPrice.textContent = formatMoney(
            purchaseType === 'subscription'
              ? subscriptionPriceFor(data, isSelected)
              : oneTimePriceFor(data)
          );
        }
      });
    };

    const updateMobileQuantity = () => {
      const data = getCurrentQuantityData();
      if (!data) return;

      if (mobileTitle) mobileTitle.textContent = `${selectedQuantity} ${bagLabel}`;
      if (mobileServings) {
        mobileServings.textContent = `${selectedQuantity * servingsPerUnit} Servings`;
      }
      if (mobileCompare) mobileCompare.textContent = formatMoney(data.compare);
      if (mobilePrice) {
        mobilePrice.textContent = formatMoney(
          purchaseType === 'subscription'
            ? subscriptionPriceFor(data, true)
            : oneTimePriceFor(data)
        );
      }

      if (mobileBadge) {
        if (selectedQuantity === 2 && popularBadge) {
          mobileBadge.textContent = popularBadge;
          mobileBadge.hidden = false;
        } else if (selectedQuantity === 3 && dealBadge) {
          mobileBadge.textContent = dealBadge;
          mobileBadge.hidden = false;
        } else {
          mobileBadge.hidden = true;
        }
      }
    };

    const updatePurchaseCards = () => {
      const subscriptionSelected =
        purchaseType === 'subscription' && !!subscriptionCard;

      subscriptionCard?.classList.toggle('is-selected', subscriptionSelected);
      oneTimeCard?.classList.toggle('is-selected', !subscriptionSelected);

      if (sellingPlanInput) sellingPlanInput.disabled = !subscriptionSelected;
      if (frequencySelect) frequencySelect.disabled = !subscriptionSelected;
    };

    const updatePrices = () => {
      const data = getCurrentQuantityData();
      if (!data) return;

      if (subscriptionCompare) {
        subscriptionCompare.innerHTML = formatMoneySplit(data.compare);
      }
      if (subscriptionPrice) {
        subscriptionPrice.innerHTML = formatMoneySplit(
          subscriptionPriceFor(data, true)
        );
      }
      if (oneTimePrice) {
        oneTimePrice.innerHTML = formatMoneySplit(oneTimePriceFor(data));
      }

      if (variantInput && data.variantId) {
        variantInput.value = String(data.variantId);
      }
      if (quantityInput) {
        quantityInput.value = String(data.cartQuantity);
      }

      setAvailability(data.available);
      updateQuantityCards();
      updateMobileQuantity();
      updatePurchaseCards();
    };

    const selectQuantity = (quantity) => {
      const data = quantityData[quantity];
      if (!data || !data.available) return;

      selectedQuantity = quantity;
      selectPlanForQuantity();
      updatePrices();

      mobileQuantity?.classList.remove('is-open');
      mobileTrigger?.setAttribute('aria-expanded', 'false');
    };

    const selectPurchaseType = (type) => {
      purchaseType = type === 'subscription' && subscriptionCard
        ? 'subscription'
        : 'onetime';

      purchaseTypeInputs.forEach((input) => {
        input.checked = input.value === purchaseType;
      });

      updatePrices();
    };

    const buildCartItems = () => {
      const data = getCurrentQuantityData();
      if (!data || !data.variantId || !data.available) return [];

      if (variantInput) variantInput.value = String(data.variantId);
      if (quantityInput) quantityInput.value = String(data.cartQuantity);

      const mainItem = {
        id: data.variantId,
        quantity: data.cartQuantity,
      };

      if (purchaseType === 'subscription') {
        const sellingPlan = Number(
          frequencySelect?.value || sellingPlanInput?.value || 0
        );
        if (sellingPlan) mainItem.selling_plan = sellingPlan;
      }

      return [mainItem];
    };

    const dispatchCartEvents = (result) => {
      document.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { source: 'listicle-buybox-a', result },
        })
      );

      window.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: { source: 'listicle-buybox-a', result },
        })
      );
    };

    const isHardSoldOutError = (error) => {
      const message = String(error?.message || '').toLowerCase();
      return (
        error?.status === 422 &&
        /sold out|unavailable|not available|cannot add/i.test(message)
      );
    };

    const isQuantityInventoryError = (error) => {
      const message = String(error?.message || '').toLowerCase();
      return (
        error?.status === 422 &&
        /not enough|inventory|stock|quantity/i.test(message)
      );
    };

    const markQuantityUnavailable = (quantity) => {
      const data = quantityData[quantity];
      if (data) data.available = false;

      if (data?.button) {
        data.button.disabled = true;
        data.button.classList.add('is-unavailable');
        data.button.setAttribute('aria-disabled', 'true');
      }

      mobileOptions
        .filter((button) => Number(button.dataset.quantity) === Number(quantity))
        .forEach((button) => {
          button.disabled = true;
          button.classList.add('is-unavailable');
          button.setAttribute('aria-disabled', 'true');
        });
    };

    Object.values(quantityData).forEach((data) => {
      if (!data.available) markQuantityUnavailable(data.quantity);
    });

    const performSubmit = async () => {
      if (!available || busy) return;

      const state = {
        quantity: selectedQuantity,
        purchaseType,
        sellingPlan: frequencySelect?.value || '',
      };

      const currentQuantityData = getCurrentQuantityData();

      if (reloadForBogoIfNeeded(root, state, currentQuantityData)) {
        setBusy(true, addingText);
        return;
      }

      const items = buildCartItems();
      if (!items.length) return;

      setBusy(true, addingText);

      try {
        if (root.dataset.clearCart === 'true') {
          await postJSON('/cart/clear.js');
        }

        const result = await postJSON('/cart/add.js', { items });
        dispatchCartEvents(result);

        const destination = root.dataset.destination || 'stay';

        if (destination === 'checkout' || destination === 'cart') {
          window.location.assign(
            buildDestinationUrl(root, destination, currentQuantityData)
          );
          return;
        }

        setBusy(true, addedText);
        if (statusMessage) {
          statusMessage.textContent = root.dataset.addedMessage || addedText;
        }

        if (root.dataset.openCartDrawer === 'true') {
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }
        }

        window.setTimeout(() => {
          setBusy(false);
        }, 1800);
      } catch (error) {
        console.error('[Listicle Buybox A]', error);

        if (isHardSoldOutError(error) || isQuantityInventoryError(error)) {
          markQuantityUnavailable(selectedQuantity);
          setBusy(false);
          setAvailability(false);
          return;
        }

        if (statusMessage) {
          statusMessage.textContent = error.message || 'Something went wrong';
        }

        if (submitButton) {
          submitButton.classList.add('is-error');
          submitButton.textContent = error.message || 'Something went wrong';
        }

        window.setTimeout(() => {
          submitButton?.classList.remove('is-error');
          if (statusMessage && available) statusMessage.textContent = '';
          setBusy(false);
        }, 2600);
      }
    };

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => updateGallery(index));
    });

    prevButton?.addEventListener('click', () => {
      updateGallery(activeGalleryIndex - 1);
    });

    nextButton?.addEventListener('click', () => {
      updateGallery(activeGalleryIndex + 1);
    });

    quantityButtons.forEach((button) => {
      button.addEventListener('click', () => {
        selectQuantity(Number(button.dataset.quantity));
      });
    });

    mobileTrigger?.addEventListener('click', () => {
      if (!mobileQuantity) return;
      const isOpen = mobileQuantity.classList.toggle('is-open');
      mobileTrigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });

    mobileOptions.forEach((button) => {
      button.addEventListener('click', () => {
        selectQuantity(Number(button.dataset.quantity));
      });
    });

    purchaseTypeInputs.forEach((input) => {
      input.addEventListener('change', () => {
        if (input.checked) selectPurchaseType(input.value);
      });
    });

    subscriptionCard?.addEventListener('click', () => {
      selectPurchaseType('subscription');
    });

    oneTimeCard?.addEventListener('click', () => {
      selectPurchaseType('onetime');
    });

  frequencySelect?.addEventListener('change', () => {
    const selectedOption = frequencySelect.selectedOptions?.[0];
    const quantity = Number(selectedOption?.dataset?.quantity || 0);

    if (quantity && quantityData[quantity]?.available) {
      selectQuantity(quantity);
      return;
    }

    if (sellingPlanInput) sellingPlanInput.value = frequencySelect.value;
    updatePrices();
  });

    document.addEventListener('click', (event) => {
      if (!mobileQuantity || mobileQuantity.contains(event.target)) return;
      mobileQuantity.classList.remove('is-open');
      mobileTrigger?.setAttribute('aria-expanded', 'false');
    });

    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      performSubmit();
    });

    const reloadState = readBogoReloadState(root);

    if (reloadState) {
      if (quantityData[Number(reloadState.quantity)]) {
        selectedQuantity = Number(reloadState.quantity);
      }

      if (reloadState.purchaseType === 'onetime' || subscriptionCard) {
        purchaseType = reloadState.purchaseType || purchaseType;
      }

      selectPlanForQuantity();

      if (reloadState.sellingPlan && frequencySelect) {
        const planOption = Array.from(frequencySelect.options).find(
          (option) => option.value === String(reloadState.sellingPlan)
        );
        if (planOption) frequencySelect.value = planOption.value;
      }

      if (sellingPlanInput && frequencySelect) {
        sellingPlanInput.value = frequencySelect.value;
      }
    } else {
      selectPlanForQuantity();
    }

    updateDeliveryDate();
    updateGallery(0);
    updatePrices();
    setAvailability(available);

    if (reloadState) {
      restoreScroll(reloadState);
      if (reloadState.submit) {
        window.setTimeout(performSubmit, 60);
      }
    }
  };

  const initAll = (container = document) => {
    container.querySelectorAll(ROOT_SELECTOR).forEach(initBuybox);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();
